import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast, QueryLoader, Flex, Text, Banner } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useComplianceCaseContext } from "../../../entrypoints/ComplianceCaseContext";
import {
  ComplianceObjectType,
  DocumentUploadInviteStatus,
} from "../../../graphql/supergraphEnums";
import {
  useGetDocumentDetailsQuery,
  useCancelUploadInviteMutation,
  useResendUploadInviteMutation,
} from "../../../graphql/supergraphHooks";
import {
  type GetDocumentDetailsQuery,
  type DocumentType,
} from "../../../graphql/supergraphTypes";
import useDocumentProcessor from "../../../hooks/documents/useDocumentProcessor";
import useUpdateDocumentsCompleted from "../../../hooks/documents/useUpdateDocumentsCompleted";
import {
  isIndividualAccountType,
  isJoinAccountType,
  resolvedAccountType,
} from "../../../utils/account_types";
import { formatDate } from "../../../utils/date_formatter";
import { getErrorMessage } from "../../../utils/error_parser";
import { clientContext } from "../../../utils/graphql_utils";
import { formatCountText } from "../../../utils/text_utils";
import { DefaultModal } from "../../common/DefaultModal";
import { provideLegalName } from "../../ProfileTree/utils";
import { DocumentFormProps } from "../DocumentCollector/types";
import {
  inviteCompleteWrapper,
  inviteCompleteMessageText,
  buttonText,
  buttonTextDisabled,
} from "../DocumentCollectorInvestor/style_vanilla.css";
import { DocumentDetails } from "../DocumentDetails";
import { mapDocumentsToFormProps } from "../DocumentDetails/utils";
import { isDocumentExpired } from "../utils";

const useComplianceProfileData = (
  data: GetDocumentDetailsQuery | undefined,
  caseId: number,
  skipDetailsForRootProfile: boolean,
) => {
  const complianceProfile = useMemo(() => data?.complianceProfile, [data]);

  const complianceCase = useMemo(
    () =>
      complianceProfile?.complianceCases?.find(
        (complianceCase) => complianceCase.caseId === caseId,
      ),
    [complianceProfile?.complianceCases, caseId],
  );

  const profileName = useMemo(() => provideLegalName(complianceProfile), [complianceProfile]);

  const investingProfile = useMemo(
    () => complianceCase?.complianceCase?.investingProfile,
    [complianceCase?.complianceCase?.investingProfile],
  );

  const isInvestingProfile = useMemo(
    () => Boolean(complianceCase?.isInvestingProfile),
    [complianceCase?.isInvestingProfile],
  );

  const investorAccountType = useMemo(
    () => resolvedAccountType(investingProfile),
    [investingProfile],
  );

  const isIndividualInvestor = useMemo(
    () =>
      Boolean(
        isIndividualAccountType(investorAccountType) ||
          investingProfile?.complianceObjectType === ComplianceObjectType.Individual,
      ),
    [investingProfile?.complianceObjectType, investorAccountType],
  );

  const documentsOnlyScenario = useMemo(
    () => isIndividualInvestor && skipDetailsForRootProfile,
    [isIndividualInvestor, skipDetailsForRootProfile],
  );

  const showInviteButton = useMemo(
    () => !(isInvestingProfile || isJoinAccountType(investorAccountType)),
    [isInvestingProfile, investorAccountType],
  );

  const documentDetails = useMemo(() => {
    const documentTypes = isJoinAccountType(investorAccountType)
      ? investingProfile?.rulesEngine?.documentTypes
      : complianceProfile?.rulesEngine?.documentTypes;

    const documents = (complianceProfile?.documents || []) as DocumentType[];
    return mapDocumentsToFormProps(documentTypes || [], documents);
  }, [
    complianceProfile?.rulesEngine?.documentTypes,
    investingProfile?.rulesEngine?.documentTypes,
    complianceProfile?.documents,
    investorAccountType,
  ]);

  return {
    profileName,
    showInviteButton,
    documentDetails,
    documentsOnlyScenario,
    isIndividualInvestor,
  };
};

type DocumentDetailsInvestorProps = {
  onStepCompletion: () => void;
};

export const DocumentDetailsInvestor: React.FC<DocumentDetailsInvestorProps> = ({
  onStepCompletion,
}) => {
  const history = useHistory();
  const { profileId } = useParams<{ profileId: string }>();
  const { rootProfileId, basePath, caseId, skipDetailsForRootProfile } =
    useComplianceCaseContext();

  const [invitedUser, setInvitedUser] = useState<{ id: number; emailAddress: string } | null>(
    null,
  );

  const parsedProfileId = profileId ? parseInt(profileId) : rootProfileId;
  const isRoot = parsedProfileId === rootProfileId;

  const documentDetailsResult = useGetDocumentDetailsQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      profileId: parsedProfileId,
      caseId: caseId,
    },
  });

  const [cancelUploadInvite] = useCancelUploadInviteMutation({
    refetchQueries: ["GetDocumentDetails"],
    awaitRefetchQueries: true,
  });

  const [resendUploadInvite, { loading: isResending }] = useResendUploadInviteMutation({
    refetchQueries: ["GetDocumentDetails"],
    awaitRefetchQueries: true,
  });

  const { processDocuments, loading: isSubmitting } = useDocumentProcessor(
    caseId,
    parsedProfileId,
  );

  const { updateDocumentsCompleted, loading: updateDocumentsCompletedLoading } =
    useUpdateDocumentsCompleted(caseId, parsedProfileId);

  const {
    profileName,
    showInviteButton,
    documentDetails,
    documentsOnlyScenario,
    isIndividualInvestor,
  } = useComplianceProfileData(documentDetailsResult.data, caseId, skipDetailsForRootProfile);

  const inviteDetails = useMemo(
    () => documentDetailsResult.data?.complianceProfile?.inviteToUploadDocumentsDetails,
    [documentDetailsResult.data?.complianceProfile?.inviteToUploadDocumentsDetails],
  );

  const statusBannerInfo = useMemo(() => {
    switch (inviteDetails?.status) {
      case DocumentUploadInviteStatus.UploadPending:
        return {
          message: `${inviteDetails.emailAddress} was invited to complete on ${formatDate(inviteDetails?.emailInviteDateSent as string)}`,
          inviteId: inviteDetails?.id,
        };
      case DocumentUploadInviteStatus.UploadSkipped:
        return {
          message: `${inviteDetails.emailAddress} indicated they could not provide any requested documents on ${formatDate(inviteDetails?.documentUploadDate as string)}`,
        };
      case DocumentUploadInviteStatus.UploadCompleted:
        return {
          message: `${inviteDetails.emailAddress} uploaded documents on ${formatDate(inviteDetails?.documentUploadDate as string)}`,
        };
      default:
        return null;
    }
  }, [inviteDetails]);

  const handleSave = useCallback(
    (
      onUploadComplete: () => void,
      currentDocument: DocumentFormProps | null,
      deletedDocId?: number | null,
    ) => {
      if (currentDocument || deletedDocId) {
        processDocuments(
          currentDocument ? [currentDocument] : [],
          deletedDocId ? [deletedDocId] : [],
          false,
        )
          .then(() => {
            if (currentDocument) {
              toast.success("Document uploaded successfully");
            } else if (deletedDocId) {
              toast.success("Document deleted successfully");
            }
            onUploadComplete();
          })
          .catch((error) => {
            toast.error(getErrorMessage(error));
          });
      }
    },
    [processDocuments],
  );

  const handleFormCompletion = useCallback(async () => {
    await updateDocumentsCompleted();

    if (isIndividualInvestor) {
      documentsOnlyScenario ? onStepCompletion() : history.goBack();
    } else {
      history.push(isRoot ? basePath : `${basePath}/overview/${parsedProfileId}`);
    }
  }, [
    updateDocumentsCompleted,
    isIndividualInvestor,
    documentsOnlyScenario,
    onStepCompletion,
    history,
    isRoot,
    basePath,
    parsedProfileId,
  ]);

  const handleInviteSent = useCallback(() => {
    void documentDetailsResult.refetch();
  }, [documentDetailsResult]);

  const handleResendInvite = useCallback(
    (inviteId: number) => {
      void resendUploadInvite({
        variables: {
          inviteId,
        },
      })
        .then(() => {
          toast.success("Invite resent successfully");
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
        });
    },
    [resendUploadInvite],
  );

  const handleCancelInvite = useCallback(
    (inviteId: number) => {
      setInvitedUser({
        id: inviteId,
        emailAddress: inviteDetails?.emailAddress || "",
      });
    },
    [inviteDetails?.emailAddress],
  );

  const handleConfirmCancelInvite = useCallback(() => {
    if (!invitedUser) {
      return;
    }
    void cancelUploadInvite({
      variables: {
        inviteId: invitedUser.id,
      },
    }).then(() => {
      setInvitedUser(null);
    });
  }, [invitedUser, cancelUploadInvite]);

  const canShowInvite = useMemo(
    () => showInviteButton && !!(caseId && parsedProfileId),
    [showInviteButton, caseId, parsedProfileId],
  );

  const hasGuestUpload = documentDetails.some((doc) => doc.uploaderInfo != null);

  const expiredDocuments = useMemo(() => {
    return documentDetails.filter((doc) => doc.file && isDocumentExpired(doc.expiryDate));
  }, [documentDetails]);

  const allDocumentsComplete = useMemo(() => {
    return (
      documentDetails.every((doc) => doc.file != null || doc.uploaderInfo != null) &&
      expiredDocuments.length === 0
    );
  }, [documentDetails, expiredDocuments.length]);

  const disableInviteButton =
    inviteDetails?.status === DocumentUploadInviteStatus.UploadPending ||
    hasGuestUpload ||
    allDocumentsComplete;

  const expiredDocumentsBanner = useMemo(() => {
    if (expiredDocuments.length === 0) return null;

    if (expiredDocuments.length === 1) {
      const doc = expiredDocuments[0];
      const lastUploaded = doc.createdAt ? formatDate(doc.createdAt) : null;
      return (
        <Banner
          variant="warning"
          title={`${doc.title} has expired`}
          description={`Upload the latest document to meet regulatory requirements.${lastUploaded ? ` The expired document was last uploaded on ${lastUploaded}.` : ""}`}
        />
      );
    }

    return (
      <Banner
        variant="warning"
        title={formatCountText(expiredDocuments.length, "expired document")}
        description="Please provide current documentation for all records that have expired"
      />
    );
  }, [expiredDocuments]);

  const statusBanner = statusBannerInfo ? (
    <Flex className={inviteCompleteWrapper}>
      <Text variant="regular-md" className={inviteCompleteMessageText}>
        {statusBannerInfo.message}
      </Text>
      {statusBannerInfo.inviteId && (
        <Flex gap={Sizes.SM}>
          <Text
            variant="strong-md"
            className={isResending ? buttonTextDisabled : buttonText}
            onClick={() => {
              if (statusBannerInfo.inviteId && !isResending) {
                handleResendInvite(statusBannerInfo.inviteId);
              }
            }}
          >
            {isResending ? "Resending..." : "Resend"}
          </Text>
          <Text
            variant="strong-md"
            className={buttonText}
            onClick={() => {
              if (statusBannerInfo.inviteId) {
                handleCancelInvite(statusBannerInfo.inviteId);
              }
            }}
          >
            Cancel
          </Text>
        </Flex>
      )}
    </Flex>
  ) : null;

  return (
    <QueryLoader result={documentDetailsResult}>
      <DocumentDetails
        documentDetails={documentDetails}
        isSubmitting={isSubmitting || updateDocumentsCompletedLoading}
        profileName={profileName}
        canShowInvite={canShowInvite}
        disableInviteButton={disableInviteButton}
        caseId={caseId}
        profileId={parsedProfileId}
        onInviteSent={handleInviteSent}
        statusBanner={statusBanner}
        expiredDocumentsBanner={expiredDocumentsBanner}
        allDocumentsComplete={allDocumentsComplete}
        updateDocument={handleSave}
        onCompletion={() => void handleFormCompletion()}
      />
      <DefaultModal
        show={!!invitedUser}
        title="Warning"
        body={`Are you sure you want to cancel the invitation to complete sent to ${invitedUser?.emailAddress}? A new invitation can be sent once this invitation is canceled.`}
        negBtnText="Close"
        posBtnText="Cancel invitation"
        posBtnVariant="danger"
        onClose={() => setInvitedUser(null)}
        onConfirm={handleConfirmCancelInvite}
      />
    </QueryLoader>
  );
};
